import React, { useState, useEffect, useMemo } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";

// @mui material components
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Icon from "@mui/material/Icon";

// Material Dashboard components
import MDBox from "mdComponents/MDBox";
import Sidenav from "components/Sidenav";
import Configurator from "components/Configurator";

// Themes
import theme from "assets/theme";
import themeRTL from "assets/theme/theme-rtl";
import themeDark from "assets/theme-dark";
import themeDarkRTL from "assets/theme-dark/theme-rtl";

// RTL
import rtlPlugin from "stylis-plugin-rtl";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";

// Routes
import getRoutes from "routes";

// Contexts
import { useMaterialUIController, setMiniSidenav, setOpenConfigurator } from "context";
import { useUser } from "context/UserContext";

// Images
import brandWhite from "assets/images/logo-ct.png";
import brandDark from "assets/images/logo-ct-dark.png";
import { APP_NAME } from "./constants";
import { MainLogo } from "./components/Icons/MainLogo";
import MDTypography from "mdComponents/MDTypography";

const App: React.FC = () => {
  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    direction,
    layout,
    openConfigurator,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode,
  } = controller;

  const { pathname } = useLocation();
  const { isLoggedIn, userCorporateAccount, authLoading } = useUser();

  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [rtlCache, setRtlCache] = useState<any>(null);

  // Create RTL cache once
  useMemo(() => {
    const cacheRtl = createCache({
      key: "rtl",
      stylisPlugins: [rtlPlugin],
    });
    setRtlCache(cacheRtl);
  }, []);

 const routesArray = useMemo(() => getRoutes(), []);


  // Handle sidenav mouse events
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);

  // Set body direction
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  // Scroll to top on route change
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement!.scrollTop = 0;
  }, [pathname]);

  // Show a loading screen until auth restores session
  // if (authLoading) {
  //   return (
  //     <ThemeProvider theme={darkMode ? themeDark : theme}>
  //       <CssBaseline />
  //       <MDBox
  //         display="flex"
  //         justifyContent="center"
  //         alignItems="center"
  //         height="100vh"
  //         fontSize="1.5rem"
  //       >
  //         <MainLogo />
  //         <MDBox>Loading...</MDBox>
  //       </MDBox>
  //     </ThemeProvider>
  //   );
  // }

  const themeToUse = darkMode
    ? direction === "rtl"
      ? themeDarkRTL
      : themeDark
    : direction === "rtl"
    ? themeRTL
    : theme;

  const content = (
    <>
      {layout === "dashboard" && isLoggedIn && (
        <>
          <Sidenav
            color={sidenavColor}
            brand={(transparentSidenav && !darkMode) || whiteSidenav ? brandDark : brandWhite}
            brandName={userCorporateAccount ? userCorporateAccount.businessName : APP_NAME}
            routes={routesArray}
            onMouseEnter={handleOnMouseEnter}
            onMouseLeave={handleOnMouseLeave}
          />
          <Configurator />
        </>
      )}
      {layout === "vr" && <Configurator />}
      <Routes>
        {routesArray.map((route) => {
          if (route.collapse) {
            return route.collapse.map((r) => (
              <Route key={r.key} path={r.route} element={r.component} />
            ));
          }
          if (route.route) {
            return <Route key={route.key} path={route.route} element={route.component} />;
          }
          return null;
        })}
        <Route
          path="*"
          element={<Navigate to="/authentication/sign-in" replace />}
        />
      </Routes>
    </>
  );

  return direction === "rtl" ? (
    <CacheProvider value={rtlCache}>
      <ThemeProvider theme={themeToUse}>
        <CssBaseline />
        {content}
      </ThemeProvider>
    </CacheProvider>
  ) : (
    <ThemeProvider theme={themeToUse}>
      <CssBaseline />
      {content}
    </ThemeProvider>
  );
};

export default App;
